import { FC } from 'react';
import {
  Control,
  Controller,
  useFieldArray,
  UseFormGetValues,
} from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { useUploadFile } from 'store/files';
import { Box, Stack } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { SystemColors } from 'melp-design/style';
import {
  Button,
  FileInput,
  FileLink,
  ImageList,
  SortableList,
  TextInput,
  Typography,
  VideoInput,
} from 'melp-design/components';
import { Values } from '../../survey-content.types';

const AttachmentsList: FC<{
  control: Control<Values>;
  questionIndex: number;
  translationIndex: number;
  disabled?: boolean;
}> = ({ control, questionIndex, translationIndex, disabled }) => {
  const { t } = useTranslation();

  const {
    fields: attachmentFields,
    append: appendAttachment,
    move: moveAttachments,
    remove: removeAttachment,
  } = useFieldArray({
    control,
    name: `questions.${questionIndex}.translations.${translationIndex}.attachments`,
  });

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  return (
    <Controller
      name={`questions.${questionIndex}.translations.${translationIndex}.attachments`}
      control={control}
      rules={{
        required: {
          value: true,
          message: t('form.required'),
        },
      }}
      render={({ field: { name, value, onChange, ref }, fieldState }) => (
        <Stack gap={1}>
          <FileInput
            name={name}
            label={t('form.attachments')}
            onChange={([file]) => {
              if (file) {
                uploadFile(
                  { file },
                  {
                    onSuccess: ({ data }) => {
                      appendAttachment({ fileId: data.id, name: file.name });
                    },
                  },
                );
              } else {
                onChange(value);
              }
            }}
            disabled={isUploading || disabled}
            maxFiles={1}
            previewSelectedFiles={false}
            accept={[
              'jpg',
              'jpeg',
              'png',
              'gif',
              'webp',
              'txt',
              'pdf',
              'doc',
              'docx',
              'xls',
              'xlsx',
              'ppt',
              'pptx',
            ]}
            error={fieldState.error?.root?.message || fieldState.error?.message}
            ref={ref}
          />

          <SortableList
            key={JSON.stringify(attachmentFields.map((field) => field.id))}
            onPositionChange={moveAttachments}
            disabled={disabled}
            items={attachmentFields.map((field, i) => ({
              id: field.id,
              content: (
                <Stack
                  direction="row"
                  alignItems="center"
                  py={0.5}
                  px={2}
                  spacing={2}
                  sx={{ backgroundColor: SystemColors.white }}
                  borderRadius={1}
                  boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                  key={field.id}
                >
                  <SortableList.DragIndicator />

                  <Box flex={1}>
                    <FileLink id={field.fileId} />
                  </Box>

                  <Box flexShrink={0}>
                    <Button
                      variant="neutral-outline"
                      size="md"
                      icon={Delete}
                      onClick={() => removeAttachment(i)}
                      title={t('common.remove')}
                      disabled={disabled}
                    />
                  </Box>
                </Stack>
              ),
            }))}
          />
        </Stack>
      )}
    />
  );
};

export const QuestionTranslationsList: FC<{
  control: Control<Values>;
  questionIndex: number;
  getValues: UseFormGetValues<Values>;
  disabled?: boolean;
}> = ({ control, questionIndex, getValues, disabled }) => {
  const { t } = useTranslation();
  const { isFeatureEnabled } = useMe();
  const { defaultLanguage } = useCompanyLanguages();

  const { fields } = useFieldArray({
    name: `questions.${questionIndex}.translations`,
    control,
  });

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const [
    selectedLanguage,
    withDescription,
    withImage,
    withAttachments,
    withVideo,
    questionType,
  ] = getValues([
    'ui.selectedLanguage',
    `questions.${questionIndex}.withDescription`,
    `questions.${questionIndex}.withImage`,
    `questions.${questionIndex}.withAttachments`,
    `questions.${questionIndex}.withVideo`,
    `questions.${questionIndex}.type`,
  ]);

  return (
    <>
      {fields.map((field, i) => (
        <Box
          key={field.id}
          sx={{
            display: selectedLanguage === field.language ? 'block' : 'none',
            minWidth: 0,
          }}
        >
          <Stack gap={2}>
            <Controller
              name={`questions.${questionIndex}.translations.${i}.text`}
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('form.required'),
                },
              }}
              render={({
                field: { name, value, onChange, ref },
                fieldState,
              }) => {
                const defaultTranslation =
                  getValues(`questions.${questionIndex}.translations`).find(
                    (t) => t.language === defaultLanguage,
                  )?.text ?? '';

                return (
                  <TextInput
                    label={
                      questionType === 'information_page'
                        ? t('common.name')
                        : t('surveys.question')
                    }
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    emoji
                    rows={2}
                    errorMessage={fieldState.error?.message}
                    infoMessage={
                      defaultLanguage === field.language
                        ? undefined
                        : `${t(
                            'wallet.employeeWalletFundsSource',
                          )} (${defaultLanguage}): ${defaultTranslation}`
                    }
                    sx={{ width: '100%' }}
                    ai={
                      isFeatureEnabled('ai')
                        ? {
                            inputLanguage: defaultLanguage,
                            outputLanguage: field.language,
                            text: defaultTranslation,
                          }
                        : undefined
                    }
                    ref={ref}
                  />
                );
              }}
            />

            {withDescription ? (
              <Controller
                name={`questions.${questionIndex}.translations.${i}.description`}
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  const defaultTranslation =
                    getValues(`questions.${questionIndex}.translations`).find(
                      (t) => t.language === defaultLanguage,
                    )?.description ?? '';

                  return (
                    <TextInput
                      label={t('employees.details')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={disabled}
                      infoMessage={
                        defaultLanguage === field.language
                          ? undefined
                          : `${t(
                              'wallet.employeeWalletFundsSource',
                            )} (${defaultLanguage}): ${defaultTranslation}`
                      }
                      emoji
                      rows={3}
                      ai={
                        isFeatureEnabled('ai')
                          ? {
                              inputLanguage: defaultLanguage,
                              outputLanguage: field.language,
                              text: defaultTranslation,
                            }
                          : undefined
                      }
                      ref={ref}
                    />
                  );
                }}
              />
            ) : null}

            {withImage ? (
              <Controller
                name={`questions.${questionIndex}.imageId`}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('form.required'),
                  },
                }}
                render={({
                  field: { name, value, onChange, ref },
                  fieldState,
                }) => {
                  const defaultTranslation = getValues(
                    `questions.${questionIndex}.translations`,
                  ).find((t) => t.language === defaultLanguage);

                  return value ? (
                    <Box>
                      <Typography color="textSecondary">
                        {t('form.image')}
                      </Typography>
                      <ImageList
                        items={[
                          {
                            imageId: value,
                            actions: [
                              {
                                icon: Delete,
                                variant: 'neutral-outline',
                                disabled,
                                onClick: () => {
                                  onChange('');
                                },
                              },
                            ],
                          },
                        ]}
                      />
                    </Box>
                  ) : (
                    <FileInput
                      name={name}
                      label={t('form.image')}
                      onChange={([file]) => {
                        if (file) {
                          uploadFile(
                            { file },
                            {
                              onSuccess: ({ data }) => {
                                onChange(data.id);
                              },
                            },
                          );
                        } else {
                          onChange('');
                        }
                      }}
                      disabled={isUploading || disabled}
                      maxFiles={1}
                      previewSelectedFiles={false}
                      accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                      error={fieldState.error?.message}
                      ai={
                        isFeatureEnabled('ai')
                          ? {
                              title: defaultTranslation?.text ?? '',
                              aspectRatio: [16, 9],
                            }
                          : undefined
                      }
                      ref={ref}
                    />
                  );
                }}
              />
            ) : null}

            {questionType === 'information_page' && withVideo ? (
              <Controller
                name={`questions.${questionIndex}.translations.${i}.videos`}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <VideoInput
                    label={t('form.video_from_link')}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    maxVideos={1}
                  />
                )}
              />
            ) : null}

            {withAttachments ? (
              <AttachmentsList
                control={control}
                questionIndex={questionIndex}
                translationIndex={i}
                disabled={disabled}
              />
            ) : null}
          </Stack>
        </Box>
      ))}
    </>
  );
};
